/**
 * `/v1/calendar` — the one endpoint this page has of its own.
 *
 * The day detail is deliberately absent from this file. It is `tasksQueryOptions` with a
 * `dueFrom`/`dueTo` of one day, because `contracts/calendar.ts` says so in as many words: "the
 * day detail is not a special endpoint, it is the list everybody already has, asked for one
 * day." A second fetch function here would be a second page size, a second sort and a second
 * thing to keep in step with the task list.
 *
 * WHY `CACHE.list` AND NOT `CACHE.reference`. The counts are a live picture of the work, and
 * `overdueCount` changes at midnight without anybody touching anything. Thirty seconds is the
 * same freshness the task list has, which is right: the two are the same data counted twice.
 */
import { calendarResponseSchema, type CalendarResponse } from '@tvitvi/shared';
import { queryOptions } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk, type CalendarWindow } from '@/api/query-keys';

export function calendarQueryOptions(orgId: string, window: CalendarWindow) {
  return queryOptions({
    queryKey: qk.calendarWindow(orgId, window),
    queryFn: ({ signal }): Promise<CalendarResponse> =>
      apiRequest({
        path: '/calendar',
        /*
         * `editionId` ONLY WHEN THE WINDOW CARRIES ONE, and the two callers differ in exactly
         * that. The totalkalender is the organisation's whole calendar and sends nothing — the
         * contract's own note on the parameter calls its absence "every edition's tasks and the
         * organisation's unscoped ones". The årgang's calendar sends the year it is standing in.
         *
         * Spread rather than assigned, for the reason `tasksQueryOptions` spells out: a key
         * present with `undefined` is dropped by `buildUrl` but is still a key, and "the
         * organisation's calendar carries no year filter" has to be assertable on the request.
         */
        query: {
          from: window.from,
          to: window.to,
          ...(window.editionId !== undefined ? { editionId: window.editionId } : {}),
        },
        schema: calendarResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}
